import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  AuthenticationService,
  BaseComponent,
  CredentialsService,
  I18nService,
  Logger,
} from '@app/core';
import { environment } from '@env/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { ThemingService } from './core/helperclasses/theming.service';
import { TranslationsService } from './core/translations.service';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MY_DATE_FORMATS } from './models';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AlertDialogComponent } from './dialogs/alert-dialog/alert-dialog.component';
import {
  SURVEY_SERVICE,
  SurveyServiceInterface,
} from './core/survey-service-interface';
import { TrackingService } from './core/tracking.service';

const log = new Logger('App');

const appHeight = () => {
  const doc = document.documentElement;
  if (window.innerHeight) {
    doc.style.setProperty('--app-height', `${window.innerHeight}px`);
  } else {
    doc.style.setProperty('--app-height', `100vh`);
  }
};
@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {
  public isInit = false;
  private inactivityTimeout = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private inactivityTimer!: any;
  private logoutTimeout = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private logoutTimer!: any;

  constructor(
    @Inject(SURVEY_SERVICE) surveyService: SurveyServiceInterface,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private translationService: TranslationsService,
    private i18nService: I18nService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private credentialsService: CredentialsService,
    private translateService: TranslateService,
    private themingService: ThemingService,
    private authService: AuthenticationService,
    private dialog: MatDialog,
    // Injektion startet die Bewegungsdaten-Erfassung (Router-Abo + Flush-Timer).
    private trackingService: TrackingService,
  ) {
    super();
    this.loadIcons();
    window.addEventListener('resize', appHeight);
  }

  ngOnInit() {
    appHeight();

    registerLocaleData(localeDe, 'de-DE', localeDeExtra);

    this.setHtmlThemeClass('theme-light');
    this.themingService.theme.subscribe((theme: string) => {
      this.setHtmlThemeClass(theme);
    });

    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    log.debug('init');

    // Setup translations
    this.i18nService.init(
      environment.defaultLanguage,
      environment.supportedLanguages,
    );

    const onNavigationEnd = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    );

    // Change page title on navigation or language change, based on route data
    merge(this.translateService.onLangChange, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => route.data),
        untilDestroyed(this),
      )
      .subscribe((event) => {
        const title = event['title'];
        if (title) {
          this.titleService.setTitle(this.translateService.instant(title));
        }
      });

    this.loadIcons();
    this.setFavicon();

    this.addSubscription(this.authService.$forceLogout()).subscribe((res) => {
      if (res) {
        const forceLogout = JSON.parse(res);
        this.inactivityTimeout = forceLogout.inactive_after_minutes * 60 * 1000;
        this.logoutTimeout = forceLogout.force_logout_after_minutes * 60 * 1000;
        this.resetInactivityTimer();
      }
    });
    this.authService.forceLogout.next(localStorage.getItem('forceLogout'));
  }

  override ngOnDestroy() {
    this.i18nService.destroy();
  }

  setFavicon(): void {
    const link: HTMLLinkElement =
      document.querySelector("link[rel*='icon']") ||
      document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = `${environment.serverUrl + environment.apiVersion}/general/favicon`;
    document.getElementsByTagName('head')[0].appendChild(link);
  }

  loadIcons() {
    //Menu Icons
    this.iconRegistry.addSvgIcon(
      'dashboard_24',
      this.sanitizer.bypassSecurityTrustResourceUrl(
        window.location.origin + '/assets/material/icons/dashboard-24px.svg',
      ),
    );
  }

  setHtmlThemeClass(themeClass: string) {
    const htmlTag = document.documentElement;
    htmlTag.classList.remove('theme-light');
    htmlTag.classList.remove('theme-dark');

    htmlTag.classList.add(themeClass);
  }

  resetInactivityTimer() {
    clearTimeout(this.inactivityTimer);
    clearTimeout(this.logoutTimer);
    let alertDialog: MatDialogRef<AlertDialogComponent>;
    this.inactivityTimer = setTimeout(() => {
      this.credentialsService.forceLogoutWarning$.next(true);
      alertDialog = this.dialog.open(AlertDialogComponent, {
        data: {
          title: this.translateService.instant('force_logout_title'),
          body: this.translateService.instant('force_logout_message'),
          actions: [
            {
              index: 'cancel',
              label: 'stay_logged_in',
            },
          ],
        },
      });
    }, this.inactivityTimeout);

    this.logoutTimer = setTimeout(() => {
      clearTimeout(this.inactivityTimer);
      clearTimeout(this.logoutTimer);
      this.inactivityTimeout = 0;
      this.logoutTimeout = 0;
      this.credentialsService.forceLogoutWarning$.next(false);
      alertDialog.close();
      this.authService.logout(true);
    }, this.logoutTimeout);
  }

  @HostListener('document:mousemove')
  @HostListener('document:keydown')
  @HostListener('document:click')
  handleUserActivity() {
    if (this.inactivityTimeout && this.logoutTimeout) {
      this.resetInactivityTimer();
    }
  }
}
